import type { PaymentMethod } from '@/types/checkout';
import type { StripeDirectGooglePayBillingDetails } from '@/types/paymentHandlers';
import { getEnvVariable } from '@/utils/get-env-variable';
import {
  DIRECT_GOOGLE_PAY_ALLOWED_AUTH_METHODS,
  DIRECT_GOOGLE_PAY_ALLOWED_CARD_NETWORKS,
  DIRECT_GOOGLE_PAY_API_VERSION,
  DIRECT_GOOGLE_PAY_API_VERSION_MINOR,
  DIRECT_GOOGLE_PAY_FALLBACK_MERCHANT_INFO,
  DIRECT_GOOGLE_PAY_STRIPE_API_VERSION,
} from '@/constants/googlePay';

/**
 * Direct pay.js Google Pay flow for Stripe-routed wallets
 * (`checkout_stripe_direct_google_pay`).
 *
 * Why this exists: Stripe's Express Checkout Element uses a relay flow on
 * WebKit — Google navigates to a `returnUrl` on `b.stripecdn.com` which must
 * re-establish the caller context. That handshake fails at the approval step
 * on iOS (`OR_BIBED_15`). Calling `loadPaymentData` from our own page keeps
 * the `window.opener` handback Google's buyflow verifies — the architecture
 * our Adyen/Nuvei Google Pay integrations already use successfully — while
 * `tokenizationSpecification.gateway = 'stripe'` keeps tokenization and
 * processing on Stripe.
 */

export interface DirectGooglePayMerchantInfo {
  merchantId: string;
  merchantName: string;
}

export interface DirectGooglePayBillingAddress {
  name?: string;
  postalCode?: string;
  countryCode?: string;
  locality?: string;
  administrativeArea?: string;
  address1?: string;
  address2?: string;
  phoneNumber?: string;
}

export interface DirectGooglePayPaymentData {
  email?: string;
  paymentMethodData?: {
    info?: {
      billingAddress?: DirectGooglePayBillingAddress;
    };
    tokenizationData?: {
      type?: string;
      token?: string;
    };
  };
}

/**
 * Google Pay sheet environment. Normally inferred from the Stripe key
 * (pk_live → PRODUCTION), but `VITE_GOOGLE_PAY_ENV` can force it explicitly.
 * That override exists because Google's TEST environment is broken at the
 * approval step on iOS WebKit, while Stripe officially supports running the
 * PRODUCTION sheet with pk_test keys (real card in the sheet → Stripe
 * returns a TEST token, nothing is charged) — the only way to exercise the
 * real approval handback on an iPhone without live charges. The playground
 * sets it via `?__gpayEnv=` (window override); production builds never
 * define it, so behavior there is unchanged.
 */
export const resolveGooglePayEnvironment = (publishableKey?: string): 'PRODUCTION' | 'TEST' => {
  const forced = getEnvVariable('VITE_GOOGLE_PAY_ENV')?.toUpperCase?.();
  if (forced === 'PRODUCTION' || forced === 'TEST') return forced;
  return publishableKey?.startsWith('pk_live') ? 'PRODUCTION' : 'TEST';
};

/**
 * Merchant identity for the Google Pay sheet. Prefer BE-routed config (same
 * shape the Adyen googlepay method uses: `paymentMethodConfig.configuration`),
 * then `publicConfig`, then Appcharge's own registration as a last resort.
 */
export const resolveDirectGooglePayMerchantInfo = (
  providerSettings?: Pick<PaymentMethod, 'paymentMethodConfig' | 'publicConfig'> | null
): DirectGooglePayMerchantInfo => {
  const fromConfig = providerSettings?.paymentMethodConfig?.configuration;
  const fromPublic = providerSettings?.publicConfig;
  const merchantId =
    (fromConfig?.merchantId as string | undefined) ||
    (fromPublic?.googleMerchantId as string | undefined) ||
    DIRECT_GOOGLE_PAY_FALLBACK_MERCHANT_INFO.merchantId;
  const merchantName =
    (fromConfig?.merchantName as string | undefined) ||
    (fromPublic?.googleMerchantName as string | undefined) ||
    DIRECT_GOOGLE_PAY_FALLBACK_MERCHANT_INFO.merchantName;
  return { merchantId, merchantName };
};

const buildBaseCardParameters = () => ({
  allowedAuthMethods: [...DIRECT_GOOGLE_PAY_ALLOWED_AUTH_METHODS],
  allowedCardNetworks: [...DIRECT_GOOGLE_PAY_ALLOWED_CARD_NETWORKS],
});

export const buildDirectStripeGooglePayIsReadyToPayRequest = () => ({
  apiVersion: DIRECT_GOOGLE_PAY_API_VERSION,
  apiVersionMinor: DIRECT_GOOGLE_PAY_API_VERSION_MINOR,
  allowedPaymentMethods: [
    {
      type: 'CARD',
      parameters: buildBaseCardParameters(),
    },
  ],
});

export const buildDirectStripeGooglePayPaymentDataRequest = ({
  publishableKey,
  merchantInfo,
  totalPrice,
  currencyCode,
  countryCode,
  walletTaxTransactionInfo,
}: {
  publishableKey: string;
  merchantInfo: DirectGooglePayMerchantInfo;
  /** Major units, max 2 decimals (Google Pay `totalPrice` constraint). */
  totalPrice: string;
  currencyCode: string;
  countryCode?: string;
  /**
   * ACDEV-25067 (`checkout_wallet_zip_tax` ON, US/CA session): itemized
   * transactionInfo from `buildGooglePayTaxTransactionInfo`. Its presence also
   * switches on the shipping-address collection Google requires to expose the
   * buyer's postal code pre-authorization (`onPaymentDataChanged` recomputes
   * tax in-sheet) — the same shape `getGooglePayComponentOptions` builds for
   * the Adyen direct integration.
   */
  walletTaxTransactionInfo?: google.payments.api.TransactionInfo;
}) => ({
  apiVersion: DIRECT_GOOGLE_PAY_API_VERSION,
  apiVersionMinor: DIRECT_GOOGLE_PAY_API_VERSION_MINOR,
  allowedPaymentMethods: [
    {
      type: 'CARD',
      parameters: {
        ...buildBaseCardParameters(),
        // FULL gives us the postal code + country the BE finalize payload
        // expects in `billing_details`.
        billingAddressRequired: true,
        billingAddressParameters: { format: 'FULL', phoneNumberRequired: false },
      },
      tokenizationSpecification: {
        type: 'PAYMENT_GATEWAY',
        parameters: {
          gateway: 'stripe',
          'stripe:version': DIRECT_GOOGLE_PAY_STRIPE_API_VERSION,
          'stripe:publishableKey': publishableKey,
        },
      },
    },
  ],
  ...(walletTaxTransactionInfo
    ? {
        // Google requires PAYMENT_AUTHORIZATION whenever SHIPPING_ADDRESS is
        // used ("Dynamic Price Updates requires the Authorize Payments
        // callback") — the client must register `onPaymentAuthorized` too.
        // Adyen's SDK adds both internally; raw pay.js must be explicit.
        callbackIntents: ['SHIPPING_ADDRESS', 'PAYMENT_AUTHORIZATION'],
        shippingAddressRequired: true,
        transactionInfo: walletTaxTransactionInfo,
      }
    : {
        transactionInfo: {
          totalPriceStatus: 'FINAL',
          totalPrice,
          currencyCode: currencyCode.toUpperCase(),
          ...(countryCode ? { countryCode: countryCode.toUpperCase() } : {}),
        },
      }),
  merchantInfo,
  emailRequired: true,
});

/**
 * With `gateway: 'stripe'`, Google returns `tokenizationData.token` as the
 * JSON of a Stripe Token object (`{ id: 'tok_...', ... }`). Returns the
 * token id, or null when the payload is malformed.
 */
export const parseStripeTokenIdFromGooglePayPaymentData = (
  paymentData?: DirectGooglePayPaymentData | null
): string | null => {
  const rawToken = paymentData?.paymentMethodData?.tokenizationData?.token;
  if (!rawToken) return null;
  try {
    const parsed = JSON.parse(rawToken) as { id?: unknown };
    return typeof parsed.id === 'string' && parsed.id ? parsed.id : null;
  } catch {
    return null;
  }
};

export const mapDirectGooglePayBillingDetails = (
  paymentData: DirectGooglePayPaymentData | undefined,
  { email, fallbackCountry }: { email?: string; fallbackCountry?: string }
): StripeDirectGooglePayBillingDetails => {
  const address = paymentData?.paymentMethodData?.info?.billingAddress;
  return {
    name: address?.name || '',
    email: paymentData?.email || email || '',
    phone: address?.phoneNumber || '',
    address: {
      line1: address?.address1 || '',
      line2: address?.address2 || '',
      city: address?.locality || '',
      state: address?.administrativeArea || '',
      postal_code: address?.postalCode || '',
      country: (address?.countryCode || fallbackCountry || '').toUpperCase(),
    },
  };
};
